<template>
    <div class="bankstatus">
        <div class="bankstatus__hint" v-if="showBankStatusHint">
            <router-link :to="{ name: 'clipboard' }">
                {{ $t('global.simulation.bankstatus-notice') }}
            </router-link>
        </div>
        <div class="bankstatus__inner wrapper wrapper--medium" :class="statusClass">
            <div class="inner--container">
                <button class="bankstatus__button bankstatus__button--info" @click="openBankstatusOverlay">
                    <app-icon icon="info" class="icon" size-px="24px" />
                </button>
                <bank-status-modal />
                <bank-status-modal-no-account />
                <div class="bankstatus-header columns is-mobile text-centered">
                    <div v-if="getPersonalData.sharedClient" class="column is-narrow">
                        <div
                            class="shared-client"
                            v-tooltip.top="{
                                ref: 'tooltipRef',
                                class: 'tooltip-shared-client',
                            }"
                        >
                            <div ref="tooltipRef" class="tooltip-content" v-html="sharedClientTooltip" />
                            <app-icon icon="shared-client" size-px="36px" />
                        </div>
                    </div>
                    <div class="column">
                        <h2 class="bankstatus-header__headline">
                            {{ statusHeadline }}
                        </h2>
                        <span class="bankstatus-header__label">
                            {{ $t('bankstatus.labels.status') }}
                        </span>
                    </div>
                    <div class="column">
                        <div
                            @click="openSpecialBankstatusOverlay"
                            class="has-pointer"
                            :class="{ 'is-dimmed': isDimmed }"
                        >
                            <h2 class="bankstatus-header__headline">{{ statusBenefit }}</h2>
                            <span class="bankstatus-header__label">
                                {{ $t('bankstatus.labels.status-benefit') }}
                            </span>
                        </div>
                    </div>
                    <div v-if="isMember" class="column is-narrow">
                        <a href="https://www.vrbank-in-holstein.de/wir-fuer-sie/mitglied-werden.html" target="_blank">
                            <div class="member">
                                <app-icon icon="member" size-px="24px" />
                                <p class="bankstatus-header__label is-marginless">
                                    Mitglied
                                </p>
                            </div>
                        </a>
                    </div>
                </div>
                <bank-status-sound-button />
                <bank-status-progress />
            </div>
            <div class="bankstatus__footer">
                <div
                    v-if="nextStatus !== ''"
                    class="bankstatus-progress-text text-centered"
                    :class="{ 'animate-pulse': showBounceAnimation }"
                >
                    {{
                        $t('bankstatus.labels.next-status', nextStatusCond, {
                            count: nextStatusCond,
                            nextStatus: $t(`bankstatus.levels.${nextStatus}.long_name`),
                            module: $tc('bankstatus.labels.module', nextStatusCond),
                            discount: specialBenefits[nextStatus],
                        })
                    }}
                    <recommendations-modal :disabled="modalDisabled" class="is-inline" />
                </div>
                <div v-else class="fake-height" />
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Helpers, { BANKSTATUS } from '@/helpers';
import dimmedBankStatus from '@/data/dimmedBankStatus.json';
import BankStatusProgress from '@/components/bankstatus/BankStatusProgress';
import BankStatusSoundButton from '@/components/bankstatus/BankStatusSoundButton';
import RecommendationsModal from '@/components/RecommendationsModal';
import AppIcon from '@/components/AppIcon';
import BankStatusModal from '@/components/bankstatus/BankStatusModal';
import EventBus from '@/eventBus';
import BankStatusModalNoAccount from '@/components/bankstatus/BankStatusModalNoAccount';

export default {
    components: {
        BankStatusModalNoAccount,
        BankStatusModal,
        AppIcon,
        RecommendationsModal,
        BankStatusSoundButton,
        BankStatusProgress,
    },
    data() {
        return {
            statusSequence: ['classic', 'bronze', 'silver', 'gold', 'platin'],
            statusConditions: [],
            conditionToStatusMapping: [],
            specialBenefits: {
                classic: 0,
                bronze: 2,
                silver: 4,
                gold: 8,
                platin: 16,
            },
        };
    },
    computed: {
        ...mapGetters({
            bankstatusCount: 'bankstatusCount',
            isPrefilled: 'isPrefilled',
            getAppModules: 'getAppModules',
            getProductById: 'getProductById',
            getProductsWithBenefits: 'getProductsWithBenefits',
            getProductField: 'getProductField',
            getBankStatus: 'getBankStatus',
            getCloudData: 'getCloudData',
            getAnimationState: 'getAnimationState',
            getClipboardProducts: 'clipboard/getClipboardProducts',
            isMember: 'isMember',
            getPersonalData: 'getPersonalData',
        }),
        // INFO: Hardcoded Holstein Feature "Gemeinschaftskonto"
        scStatus() {
            const points = this.getPersonalData.starsCount;
            if (points < 5) return 'Klassik';
            if (points < 8) return 'Bronze';
            if (points < 11) return 'Silber';
            if (points < 14) return 'Gold';
            return 'Platin';
        },
        scMissingPoints() {
            const points = this.getPersonalData.starsCount;
            if (points < 5) return 5 - points;
            if (points < 8) return 8 - points;
            if (points < 11) return 11 - points;
            if (points < 14) return 14 - points;
            return 0; // TODO: was tun im höchsten Status?????
        },
        scAdvantage() {
            const points = this.getPersonalData.starsCount;
            if (points < 5) return 0;
            if (points < 8) return 2;
            if (points < 11) return 4;
            if (points < 14) return 8;
            return 16;
        },
        sharedClientTooltip() {
            const points = this.getPersonalData.starsCount;

            function plural1(amount) {
                if (amount === 0) return 'Bausteinen';
                if (amount === 1) return 'Baustein';
                return 'Bausteinen';
            }
            function plural2(amount) {
                if (amount === 0) return 'Bausteine';
                if (amount === 1) return 'Baustein';
                return 'Bausteine';
            }
            function plural3(amount) {
                if (amount === 0) return 'fehlen';
                if (amount === 1) return 'fehlt';
                return 'fehlen';
            }

            const text = `<p style="margin-bottom: 0"><strong>Hinweis Gemeinschaftskunden:</strong> Sie befinden sich mit ${points} ${plural1(
                points
            )} im <strong>${this.scStatus}-Status</strong>.
            Sie haben daher einen Kontovorteil von <strong>${this.scAdvantage} €</strong>.
            Zur Erreichung des nächsten Status
            ${plural3(this.scMissingPoints)} Ihnen noch ${this.scMissingPoints} ${plural2(this.scMissingPoints)}.
            Ihre Simulation im HausbankBonus findet hier keine Berücksichtigung.
            Nutzen Sie den Hausbankcode Ihres Einzelkunden,
            um Ihre Produktversorgung in unserem HausbankBonus einzusehen.
            Sollten Sie diesen nicht vorliegen oder weitere Fragen zu Ihrem HausbankBonus-Status haben,
            hilft Ihnen Ihr Kundenberater gerne weiter.</p>`;

            return text;
        },
        // END Hardcoded
        showBounceAnimation() {
            return this.getAnimationState('bounceBankStatusProgressText');
        },
        modalDisabled() {
            return this.getAnimationState('showIntroSimulation');
        },
        showBankStatusHint() {
            const { external, interest } = this.getClipboardProducts;
            return external.length + interest.length > 0 && !this.$store.state.animations.showIntroSimulation;
        },
        statusCount() {
            return this.bankstatusCount.filled.length;
        },
        status() {
            return this.getBankStatus;
        },
        nextStatus() {
            let nextStatus = '';
            let currentStatus = this.status;

            for (let i = 0; i < this.statusSequence.length; i++) {
                if (this.statusSequence[i] === currentStatus) {
                    if (this.statusSequence[i + 1] != null) {
                        nextStatus = this.statusSequence[i + 1];
                    }
                }
            }

            return nextStatus;
        },
        statusCond() {
            return this.getStatusCondition(this.status);
        },
        nextStatusCond() {
            let condition = this.getStatusCondition(this.nextStatus);
            return condition - this.statusCount;
        },
        statusClass() {
            return 'status-' + this.status;
        },
        statusHeadline() {
            return this.$t(`bankstatus.levels.${this.status}.short_name`);
        },
        hasSpecialBenefitConditions() {
            if (this.getCloudData !== null) {
                return this.getCloudData.Anzahl_VR_MeinKonto > 0 || this.getCloudData.Anzahl_Mitarbeiterkonto > 0;
            } else {
                return false;
            }
        },
        statusBenefit() {
            return Helpers.formatNumberAsEuro(this.specialBenefits[this.status]);
        },
        isDimmed() {
            const exceptions = dimmedBankStatus.exclude;
            // INFO: Hardcoded key
            const productField = this.getProductField('Liquiditaet_Girokonto');
            const fullCountingAccounts = productField.products.filter((product) => {
                return !exceptions.includes(product.id);
            });

            let atLeastOneFullCountingAccount = false;
            for (const product of fullCountingAccounts) {
                if (product.options.BANK || product.options.INTEREST) {
                    atLeastOneFullCountingAccount = true;
                }
            }
            return !atLeastOneFullCountingAccount;
        },
    },
    methods: {
        getStatusCondition(status) {
            return BANKSTATUS[status.toUpperCase()].CONDITION;
        },
        getStatusConditions() {
            let conditions = {};

            this.statusSequence.forEach((status) => {
                conditions[parseInt(BANKSTATUS[status.toUpperCase()].CONDITION)] = 'status-' + status;
            });

            this.statusConditions = conditions;
        },
        openSpecialBankstatusOverlay() {
            if (this.isDimmed) {
                EventBus.$emit('modal-bankstatus-no-account');
            } else {
                EventBus.$emit('modal-bankstatus');
            }
        },
        openBankstatusOverlay() {
            EventBus.$emit('modal-bankstatus');
        },
    },
    beforeMount() {
        this.getStatusConditions();
    },
};
</script>
